import type {
  InkeepComponentInstance,
  InkeepSettings,
} from '@inkeep/cxkit-types';

import { type AuthState, AuthStatus } from '$lib/auth';
import { getUserDisplayName } from '$lib/user-name';

const INKEEP_API_KEY = import.meta.env.VITE_INKEEP_API_KEY;

// Add this type assertion to fix the type error
type InkeepWidgetWithSubmit = InkeepComponentInstance & {
  submitMessage: (message: string) => void;
};
export interface InkeepWidget {
  initialize: () => void;
  openModal: () => void;
  closeModal: () => void;
  submitMessage: (message: string) => void;
  destroy: () => void;
}

interface InkeepState {
  initialize: () => void;
  openModal: () => void;
  closeModal: () => void;
  destroy: () => void;
  submitMessage: (message: string) => void;
}
let inkeepState: InkeepState | undefined;

export const createInkeepWidget = async (authState: AuthState) => {
  await import('@inkeep/cxkit-js');

  if (!inkeepState && authState.status === AuthStatus.LoggedIn) {
    const user = authState.user;

    let widget: InkeepWidgetWithSubmit | undefined;

    const submitMessage = (message: string) => {
      widget?.submitMessage(message);
    };

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const closeModal = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const openModal = () => {};

    const config = {
      baseSettings: {
        apiKey: INKEEP_API_KEY,
        organizationDisplayName: 'Viam AI Bot',
        userProperties: {
          email: user.email ?? '',
          name: getUserDisplayName(user),
          id: user.email ?? '',
        },
        primaryBrandColor: '#000000',
        theme: {
          styles: [
            {
              key: 'fill-sidebar-height',
              type: 'style',
              value:
                '[data-part="ai-chat-wrapper"] { max-height: 100% !important; height: 100% !important; }',
            },
          ],
        },
      },
      aiChatSettings: {
        aiAssistantName: 'Viam',
        chatSubjectName: 'Viam',
        introMessage:
          "Hi!\n\nI'm an AI assistant trained on documentation, help articles, and other content. \n\nHow can I help you today?\n\n_Please do not share sensitive information such as secrets or API keys with me._",
        aiAssistantAvatar:
          'https://cdn.prod.website-files.com/62fba5686b6d47fe2a1ed2a6/62fba8f4a8ca05f38a2b497f_viam-logo-webclip.png',
        userAvatar:
          'https://storage.googleapis.com/organization-image-assets/viam-botAvatarDarkSrcUrl-1721328398594.svg',
        getHelpOptions: [
          {
            icon: { builtIn: 'IoMail' },
            name: 'Email',
            action: {
              type: 'open_link',
              url: 'mailto:support@viam.com',
            },
          },
          {
            icon: { builtIn: 'FaDiscord' },
            name: 'Discord',
            action: {
              type: 'open_link',
              url: 'https://discord.gg/viam',
            },
          },
        ],
        exampleQuestions: [
          'How do I install Viam on a single-board computer?',
          'How do I deploy a person detection model?',
          'How to ingest data from machines',
          'How can I query sensor data with third-party tools?',
        ],
      },
    } as InkeepSettings;

    const initialize = () => {
      // eslint-disable-next-line new-cap
      widget = window.Inkeep.EmbeddedChat?.(
        '#inkeepasidetarget',
        config
      ) as InkeepComponentInstance & {
        submitMessage: (message: string) => void;
      };

      document
        .querySelector('#inkeepasidetarget')
        ?.addEventListener('keydown', (event) => {
          event.stopPropagation();
        });
    };

    const destroy = () => {
      widget?.unmount();
      inkeepState = undefined;
    };

    inkeepState = { initialize, openModal, closeModal, destroy, submitMessage };
  }

  return inkeepState;
};
